import type { AvailableSlot } from '../../../services/api/swaggerSchemas';

interface PracticeLike {
  name?: string | null;
  location?: string | null;
  address?: string | null;
  phone?: string | null;
}

export interface PrintSummaryData {
  patientName: string;
  providerName: string;
  appointmentSlot: AvailableSlot | null | undefined;
  practice: PracticeLike | undefined;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function formatSlotForPrint(slot: AvailableSlot | null | undefined): string {
  if (!slot) return '';
  const date = new Date(slot.start_time);
  if (Number.isNaN(date.getTime())) return slot.start_time;

  const weekday = date.toLocaleString('en-US', { weekday: 'long' });
  const datePart = date.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${weekday}, ${datePart} at ${timePart}`;
}

function buildAddress(practice: PracticeLike | undefined): string {
  if (!practice) return '';
  const parts = [practice.location, practice.address].filter(Boolean);
  return parts.join(', ');
}

export function buildPrintHtml(data: PrintSummaryData): string {
  const { patientName, providerName, appointmentSlot, practice } = data;
  const appointmentText = formatSlotForPrint(appointmentSlot);
  const practiceName = practice?.name ?? '';
  const address = buildAddress(practice);
  const phone = practice?.phone?.trim() ?? '';
  const generatedAt = new Date().toLocaleString('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  });

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Follow-Up Appointment – ${escapeHtml(patientName)}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #1a202c;
      background: #ffffff;
      padding: 48px 56px;
      max-width: 680px;
      margin: 0 auto;
    }

    /* ── Top bar ── */
    .top-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 36px;
    }

    .org {
      font-size: 12px;
      font-weight: 700;
      color: #2b6cb0;
      text-transform: uppercase;
      letter-spacing: 0.1em;
    }

    .print-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      background: #2b6cb0;
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      padding: 9px 18px;
      border: none;
      border-radius: 6px;
      cursor: pointer;
    }
    .print-btn:hover { background: #2c5282; }
    .print-btn:focus-visible { outline: 3px solid #63b3ed; outline-offset: 2px; }

    /* ── Card ── */
    .card {
      border: 1.5px solid #bee3f8;
      border-radius: 12px;
      overflow: hidden;
    }

    .card-header {
      background: #2b6cb0;
      padding: 18px 28px;
    }

    .card-header-title {
      font-size: 13px;
      font-weight: 700;
      color: #bee3f8;
      text-transform: uppercase;
      letter-spacing: 0.09em;
      margin-bottom: 2px;
    }

    .card-header-sub {
      font-size: 11px;
      color: #90cdf4;
    }

    .card-body {
      padding: 28px 28px 32px;
      background: #f7fbff;
    }

    /* ── Patient name ── */
    .patient-name {
      font-size: 28px;
      font-weight: 700;
      color: #1a365d;
      line-height: 1.2;
      margin-bottom: 6px;
    }

    .intro-text {
      font-size: 15px;
      color: #4a5568;
      margin-bottom: 28px;
    }

    /* ── Detail rows ── */
    .detail-list {
      display: flex;
      flex-direction: column;
      gap: 0;
      border: 1px solid #bee3f8;
      border-radius: 8px;
      overflow: hidden;
    }

    .detail-row {
      display: flex;
      align-items: baseline;
      gap: 12px;
      padding: 14px 18px;
      background: #fff;
      border-bottom: 1px solid #ebf8ff;
    }

    .detail-row:last-child { border-bottom: none; }
    .detail-row:nth-child(even) { background: #f0f9ff; }

    .detail-label {
      font-size: 11px;
      font-weight: 700;
      color: #2b6cb0;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      min-width: 80px;
      flex-shrink: 0;
      padding-top: 2px;
    }

    .detail-value {
      font-size: 16px;
      font-weight: 600;
      color: #1a365d;
      line-height: 1.4;
    }

    .detail-value.large {
      font-size: 19px;
      font-weight: 700;
    }

    /* ── Footer ── */
    .footer {
      margin-top: 24px;
      font-size: 11px;
      color: #a0aec0;
      text-align: center;
    }

    @media print {
      .print-btn { display: none !important; }
      body { padding: 20px 24px; }
      .card { border-color: #bee3f8; }
    }
  </style>
</head>
<body>

  <div class="top-bar">
    <span class="org">CHS &mdash; Hospital Follow-Up Scheduling</span>
    <button class="print-btn" type="button" id="print-btn" aria-label="Print this page">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="6 9 6 2 18 2 18 9"/>
        <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
        <rect x="6" y="14" width="12" height="8"/>
      </svg>
      Print
    </button>
  </div>

  <div class="card">
    <div class="card-header">
      <div class="card-header-title">Follow-Up Appointment</div>
    </div>
    <div class="card-body">

      <div class="patient-name">${escapeHtml(patientName)}</div>
      <div class="intro-text">has a follow-up appointment scheduled with</div>

      <div class="detail-list">
        <div class="detail-row">
          <span class="detail-label">Provider</span>
          <span class="detail-value large">${escapeHtml(providerName)}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">When</span>
          <span class="detail-value large">${escapeHtml(appointmentText)}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Practice</span>
          <span class="detail-value">${escapeHtml(practiceName)}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Address</span>
          <span class="detail-value">${escapeHtml(address)}</span>
        </div>
        ${phone ? `<div class="detail-row">
          <span class="detail-label">Phone</span>
          <span class="detail-value">${escapeHtml(phone)}</span>
        </div>` : ''}
      </div>

    </div>
  </div>

  <div class="footer">Generated ${escapeHtml(generatedAt)}</div>

  <script>
    document.getElementById('print-btn').addEventListener('click', function () {
      window.print();
    });
  </script>
</body>
</html>`;
}

export function openPrintSummary(data: PrintSummaryData): void {
  const html = buildPrintHtml(data);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (win) win.focus();
  // Revoke after the browser has had time to load the blob
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
